import React, {createContext, useContext, useMemo} from 'react';

import {Ref as PanelBankSectionConfigRef} from '../../state/views/panelBankSectionConfig/types';
import {useRunSelectorResizingContextMaybe} from '../MultiRunWorkspace/RunSelectorResizingContext';
import {useWorkspaceDrawerContext} from '../WorkspaceDrawer/WorkspaceDrawerContextProvider';
import {usePanelBankConfigWithRefs} from './hooks/usePanelBankConfigWithRefs';

type WorkspaceSectionContextValue = {
  defaultMoveToSectionRef: PanelBankSectionConfigRef | undefined;
  /**
   * When toggling workspace drawer or resizing run selector,
   * redrawing panels as they resize can be very perf intensive
   * and cause jumpy/laggy behavior. To avoid this problem,
   * hide panel contents while panel bank is resizing.
   */
  shouldRenderContent: boolean;
  sectionName: string;
};

type WorkspaceSectionContextProviderProps = {
  children: React.ReactNode;
  sectionName: string;
};

const WorkspaceSectionContext = createContext<
  WorkspaceSectionContextValue | undefined
>(undefined);

export const WorkspaceSectionContextProvider = ({
  children,
  sectionName,
}: WorkspaceSectionContextProviderProps) => {
  const panelBankConfigWithRefs = usePanelBankConfigWithRefs();

  const defaultMoveToSectionRef = useMemo(() => {
    const {defaultMoveToSectionName} = panelBankConfigWithRefs.settings;
    const defaultMoveToSection = panelBankConfigWithRefs.sections.find(s => {
      return s.name === defaultMoveToSectionName;
    });
    return defaultMoveToSection?.ref;
  }, [panelBankConfigWithRefs.sections, panelBankConfigWithRefs.settings]);

  const {shouldHidePanelContents} = useWorkspaceDrawerContext();
  // Run page is not wrapped in RunSelectorResizingContextProvider since we don't render the run selector there.
  // We need to use useRunSelectorResizingContextMaybe so error isn't thrown.
  const runSelectorResizingContext = useRunSelectorResizingContextMaybe();
  const shouldRenderContent =
    !shouldHidePanelContents && !runSelectorResizingContext?.isResizing;

  const value = useMemo(
    () => ({
      shouldRenderContent,
      defaultMoveToSectionRef,
      sectionName,
    }),
    [shouldRenderContent, defaultMoveToSectionRef, sectionName]
  );

  return (
    <WorkspaceSectionContext.Provider value={value}>
      {children}
    </WorkspaceSectionContext.Provider>
  );
};

export const useWorkspaceSectionContext = () => {
  const value = useContext(WorkspaceSectionContext);

  if (value == null) {
    throw new Error(
      'useWorkspaceSectionContext must be used within a WorkspaceSectionContextProvider'
    );
  }

  return value;
};
